/**
 * What every mini-app is for, when to reach for it, and when a project is
 * ready for it — shared by the launcher, the sidebar, the agent's
 * suggest-followups prompt, and goal playbooks so the words cannot drift
 * between surfaces. Lives beside REPLO_OBJECT_MODEL as prompt knowledge;
 * chrome (icons, routes, flags) stays in the editor's miniapp registry, which
 * a unit test pins to exactly this id set.
 */

/**
 * The one prerequisite gating whether pitching an app is timely. Evaluated
 * against project facts by consumers that have them; a consumer that cannot
 * prove a prerequisite treats the app as not ready rather than guessing.
 */
export type MiniappReadiness =
  | "always"
  | "hasPublishedSite"
  | "hasIntegration"
  | "hasProducts";

export type MiniappCapability = {
  /** The app's proper name; the drift test pins it to the registry's. */
  name: string;
  /** What the app is for, in one sentence a merchant would read on a tile. */
  purpose: string;
  /** Intents that should route to this app, phrased for the agent. */
  useWhen: string[];
  /** Asks a merchant might actually type, in the user's own voice. */
  examplePrompts: string[];
  readyWhen: MiniappReadiness;
};

export const MINIAPP_CATALOG = {
  "website-builder": {
    name: "Site Builder",
    purpose: "Build and update your site, page by page",
    useWhen: [
      "the user wants to see, review, or hand-edit a page or the site itself",
      "you changed a page and the user should look at the result",
    ],
    examplePrompts: [
      "Show me my homepage",
      "Tighten up the hero section on my landing page",
    ],
    readyWhen: "always",
  },
  content: {
    name: "Content",
    purpose: "Write and publish blog posts and long-form content",
    useWhen: [
      "the user asks about blog posts, articles, or long-form SEO content",
    ],
    examplePrompts: ["Draft a blog post about our new collection"],
    readyWhen: "always",
  },
  draw: {
    name: "Draw",
    purpose: "Sketch layouts, diagrams, and ideas on an infinite canvas",
    useWhen: [
      "the user wants to sketch a layout, map an idea, or think visually before building",
    ],
    examplePrompts: ["Sketch a wireframe for my landing page"],
    readyWhen: "always",
  },
  assets: {
    name: "Assets",
    purpose:
      "Upload and organize the images, video, fonts, and audio you design with",
    useWhen: [
      "the user asks about uploading, finding, or approving images and other media",
      "you generated media the user should review",
    ],
    examplePrompts: ["Show me the product photos I uploaded"],
    readyWhen: "always",
  },
  files: {
    name: "Files",
    purpose: "Store, organize, and share your project's files and folders",
    useWhen: [
      "the user asks for a document, report, or file Replo produced or they saved",
    ],
    examplePrompts: ["Open the competitor research report from last week"],
    readyWhen: "always",
  },
  products: {
    name: "Products",
    purpose: "Design and manage the products you sell, down to each variant",
    useWhen: [
      "the user wants to design, customize, or review items in their catalog",
    ],
    examplePrompts: ["Design a hoodie with my logo on the back"],
    readyWhen: "hasProducts",
  },
  orders: {
    name: "Orders",
    purpose: "Track, fulfill, and refund customer orders",
    useWhen: [
      "the user asks about order status, fulfillment, refunds, or a specific customer's purchase",
    ],
    examplePrompts: ["Show me this week's unfulfilled orders"],
    readyWhen: "hasIntegration",
  },
  insights: {
    name: "Insights",
    purpose: "Track your traffic, conversion, and revenue",
    useWhen: [
      "the user asks what is converting, where traffic came from, or why revenue moved",
      "you shipped a change whose impact is worth watching",
    ],
    examplePrompts: ["How did my homepage do this week?"],
    readyWhen: "hasPublishedSite",
  },
  canvas: {
    name: "Canvas",
    purpose:
      "Ad and PDP creative maker: frames on an infinite canvas, brand fonts, templates, export to download, Files, or Assets",
    useWhen: [
      "the user wants to make, edit, or resize ad creatives or product tiles by hand",
    ],
    examplePrompts: ["Open my Spring launch set in Canvas"],
    readyWhen: "always",
  },
  sightglass: {
    name: "Sightglass",
    purpose: "Spy on competitor Meta ads and the landing pages behind them",
    useWhen: [
      "the user wants ad or landing-page inspiration from real competitor Meta campaigns",
    ],
    examplePrompts: ["Show me competitor Meta ads for skincare brands"],
    readyWhen: "always",
  },
  plans: {
    name: "Plans",
    purpose:
      "Turn a rough idea into a scope and checklist Replo can build from",
    useWhen: [
      "the request is a multi-step project worth scoping before building",
    ],
    examplePrompts: ["Plan out a full rebrand of my store"],
    readyWhen: "always",
  },
  tasks: {
    name: "Tasks",
    purpose:
      "Schedule recurring work like reports, restock checks, and weekly digests",
    useWhen: [
      "the user asks for something to happen weekly, nightly, or on a schedule",
      "the user wants to be told when something changes instead of checking it",
      "you just did something manual that will clearly need doing again",
    ],
    examplePrompts: ["Email me a Monday summary of last week's orders"],
    readyWhen: "always",
  },
  skills: {
    name: "Skills",
    purpose:
      "Teach Replo how you work by installing or writing skills it follows",
    useWhen: [
      "the user wants Replo to always do something a particular way, or to remember a workflow",
    ],
    examplePrompts: ["Always write product copy in my brand voice"],
    readyWhen: "always",
  },
  "brand-studio": {
    name: "Branding",
    purpose:
      "Set your logo, colors, type, and voice so everything stays on brand",
    useWhen: [
      "the user asks about brand look and feel, colors, fonts, or building a brand from their site",
    ],
    examplePrompts: ["Build my brand from my current website"],
    readyWhen: "always",
  },
  integrations: {
    name: "Integrations",
    purpose: "Connect your email, analytics, ads, and store tools",
    useWhen: [
      "the user names a tool like Klaviyo, Google Analytics, Meta, or Shopify",
      "the task needs data Replo cannot see until a tool is connected",
    ],
    examplePrompts: ["Connect my Klaviyo account"],
    readyWhen: "always",
  },
  settings: {
    name: "Settings",
    purpose: "Manage your project's name, region, currency, and domains",
    useWhen: [
      "the user wants to rename the project or change domains, region, or currency",
    ],
    examplePrompts: ["Connect my custom domain"],
    readyWhen: "always",
  },
  reploverse: {
    name: "Reploverse",
    purpose: "Wander a multiplayer pixel farm world with the team",
    useWhen: ["the user asks for it by name"],
    examplePrompts: ["Take me to Reploverse"],
    readyWhen: "always",
  },
  "hello-world": {
    name: "Hello World",
    purpose: "Try the mini-app platform with a demo app",
    useWhen: ["never suggest this; it is a development fixture"],
    examplePrompts: [],
    readyWhen: "always",
  },
} as const satisfies Record<string, MiniappCapability>;

export type CatalogMiniappId = keyof typeof MINIAPP_CATALOG;

// A widened view for callers holding an unvalidated id — an assignment, not a
// cast, so it can only exist while the catalog satisfies the record shape.
const catalogByMiniappId: Record<string, MiniappCapability> = MINIAPP_CATALOG;

/**
 * Whether this string names an app we can actually open.
 *
 * A type guard rather than a cast at each call site: catalog membership is the
 * only thing that makes a string a `CatalogMiniappId`, so that check and the
 * narrowing it justifies belong in one place.
 */
export function isCatalogMiniappId(value: unknown): value is CatalogMiniappId {
  return typeof value === "string" && Object.hasOwn(MINIAPP_CATALOG, value);
}

export function getMiniappCapability({
  miniappId,
}: {
  miniappId: string;
}): MiniappCapability | null {
  return catalogByMiniappId[miniappId] ?? null;
}

// Never pitched by the agent: a dev fixture and a diversion.
const PROMPT_EXCLUDED_MINIAPP_IDS: ReadonlySet<string> = new Set([
  "hello-world",
  "reploverse",
]);

const READINESS_PROMPT_NOTES: Record<MiniappReadiness, string | null> = {
  always: null,
  hasPublishedSite: "only once a site is published",
  hasIntegration: "only once the store or a relevant tool is connected",
  hasProducts: "only once the merchant's products are in Replo",
};

/**
 * The catalog as the agent reads it, injected into the build system prompt
 * through the harness's shared-sections mechanism (prompt-sections.ts) so the
 * agent and the human surfaces state each app identically. Static per build:
 * readiness renders as a condition for the model to weigh against what it
 * knows about the project, not something evaluated per user.
 */
export function renderMiniappCatalogForPrompt(): string {
  const appLines = Object.entries(MINIAPP_CATALOG)
    .filter(([miniappId]) => !PROMPT_EXCLUDED_MINIAPP_IDS.has(miniappId))
    .map(([miniappId, capability]) => {
      const readiness = READINESS_PROMPT_NOTES[capability.readyWhen];
      const example = capability.examplePrompts[0];
      return [
        `- ${capability.name} (\`${miniappId}\`) — ${capability.purpose}.`,
        `Reach for it when ${capability.useWhen.join("; ")}.`,
        readiness ? `Suggest it ${readiness}.` : null,
        example ? `e.g. "${example}"` : null,
      ]
        .filter(Boolean)
        .join(" ");
    });
  return [
    "## The apps you can put to work",
    "",
    "When a suggestion centers on one of these apps, name the app by its proper name in the suggestion text (the UI renders its icon inline wherever it is named) and set that suggestion's `miniappId`. At most one suggestion per set should introduce an app this conversation has not already touched, and only when it is genuinely the best next move — never pitch an app the project is not ready for.",
    "",
    ...appLines,
  ].join("\n");
}
